<template>
  <q-layout>
    <q-page-container>
      <q-page class="login-page flex flex-center" :class="{'login-page--leaving': leaving}" @mousemove="onMouseMove">

        <!-- Filtres du logo : son fond blanc devient transparent (plus c'est clair, plus c'est transparent) ;
             en mode sombre, les traits sont en plus éclaircis. Le résultat est limité à l'image (feComposite) :
             autour d'elle, le vide deviendrait noir -->
        <svg class="login-filters" aria-hidden="true">
          <filter id="login-ink" color-interpolation-filters="sRGB">
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  -0.5 -0.5 -0.5 0 1.5"/>
            <feComposite in2="SourceAlpha" operator="in"/>
          </filter>
          <filter id="login-ink-light" color-interpolation-filters="sRGB">
            <feColorMatrix type="matrix" values="-1 0 0 0 1  0 -1 0 0 1  0 0 -1 0 1  -0.5 -0.5 -0.5 0 1.5"/>
            <feComposite in2="SourceAlpha" operator="in"/>
          </filter>
        </svg>

        <!-- Décor : halos de lumière et aigrettes de pissenlit (pappus) portées par le vent -->
        <div class="login-scene" aria-hidden="true">
          <span class="login-glow login-glow--a"></span>
          <span class="login-glow login-glow--b"></span>
          <span class="login-glow login-glow--c"></span>
          <div class="login-seeds" :style="parallax">
            <span v-for="seed in seeds" :key="seed.id" class="login-seed" :style="seed.style">
              <svg class="login-seed__shape" viewBox="0 0 40 60">
                <line v-for="(ray, index) in rays" :key="index" x1="20" y1="24" :x2="ray.x" :y2="ray.y" />
                <line x1="20" y1="24" x2="20" y2="53" />
                <ellipse cx="20" cy="55.5" rx="1.5" ry="3.6" />
              </svg>
            </span>
          </div>
        </div>

        <!-- Le cadre secoue la carte à chaque échec de connexion, sans la recréer (la saisie et le curseur restent) -->
        <div class="login-frame" :class="{'login-frame--shake': shaking}" @animationend.self="shaking = false">
        <q-card class="login-card">
          <q-card-section class="login-head">
            <div class="login-logo-frame login-rise" style="--step: 0">
              <img class="login-logo" src="~assets/images/logo.png" alt="Pappus"/>
            </div>
            <div class="login-title login-rise" style="--step: 1">{{ $t("loginPage.welcome") }}</div>
            <div class="login-greeting login-rise" style="--step: 2">{{ greeting }}</div>
          </q-card-section>

          <q-card-section class="q-pt-none">
            <q-form class="q-gutter-md" @submit.prevent="_login">
              <!-- Appareil activé : seulement la connexion biométrique ; le mot de passe reste accessible en dessous -->
              <template v-if="biometricMode">
                <div v-if="deviceUser" class="login-device-user login-rise" style="--step: 3">
                  <q-icon name="account_circle" size="22px" class="q-mr-xs" />{{ deviceUser }}
                </div>
                <div class="login-rise" style="--step: 4">
                  <q-btn
                    unelevated
                    rounded
                    no-caps
                    class="full-width login-btn"
                    color="pappus"
                    icon="fingerprint"
                    :label="'Se connecter avec ' + biometric"
                    :loading="biometricLoading"
                    @click="biometricLogin()"
                  />
                </div>
              </template>

              <template v-else>
              <q-input
                outlined
                v-model="user.username"
                :label="$t('loginPage.username')"
                autocomplete="username"
                autofocus
                color="pappus"
                class="login-field login-rise"
                style="--step: 3"
              >
                <template v-slot:prepend><q-icon name="person"/></template>
              </q-input>

              <q-input
                outlined
                v-model="user.password"
                :type="showPassword ? 'text' : 'password'"
                :label="$t('loginPage.password')"
                autocomplete="current-password"
                color="pappus"
                class="login-field login-rise"
                style="--step: 4"
                :hint="capsLock ? 'Verrouillage des majuscules activé' : undefined"
                @keydown="onKey"
                @keyup="onKey"
                @blur="capsLock = false"
              >
                <template v-slot:prepend><q-icon name="lock"/></template>
                <template v-slot:append>
                  <q-icon v-if="capsLock" name="keyboard_capslock" color="warning" class="q-mr-xs"/>
                  <q-icon
                    :name="showPassword ? 'visibility_off' : 'visibility'"
                    class="cursor-pointer"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </q-input>

              </template>

              <transition name="login-error">
                <q-banner v-if="message" dense rounded class="login-error">
                  <template v-slot:avatar><q-icon name="error_outline"/></template>
                  {{ message }}
                </q-banner>
              </transition>

              <div v-if="!biometricMode" class="login-rise" style="--step: 5">
                <q-btn
                  unelevated
                  rounded
                  no-caps
                  class="full-width login-btn"
                  color="pappus"
                  :label="$t('loginPage.login')"
                  icon-right="arrow_forward"
                  :loading="loading"
                  :disable="!user.username || !user.password"
                  type="submit"
                />
              </div>

              <!-- Solution de secours : l'identifiant et le mot de passe -->
              <div v-if="biometricMode" class="text-center login-rise" style="--step: 5">
                <q-btn flat dense no-caps color="grey-8" icon="keyboard" label="Utiliser l'identifiant et le mot de passe"
                       @click="usePassword" />
              </div>

              <!-- Connexion biométrique : Face ID, empreinte, Windows Hello (si l'appareil sait vérifier l'utilisateur) -->
              <div v-else-if="biometric" class="login-rise" style="--step: 6">
                <q-btn
                  outline
                  rounded
                  no-caps
                  class="full-width login-biometric"
                  color="pappus"
                  icon="fingerprint"
                  :label="'Se connecter avec ' + biometric"
                  :loading="biometricLoading"
                  :disable="loading"
                  @click="biometricLogin()"
                />
              </div>
            </q-form>
          </q-card-section>

          <q-card-section class="login-foot login-rise" style="--step: 6">
            <span>{{ computedAppTarget }}</span>
            <span>🖤 Fur Tu Guru 🖤</span>
          </q-card-section>
        </q-card>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script>
import User from "src/models/user";
import {useAuthStore} from "../stores/authStore";
import AuthService from "src/services/authService"

import {mapStores} from "pinia";
import AppEnvService from "../services/utils/appEnvService";
import WebauthnService from "src/services/webauthnService";
import NavigationService from "../services/navigationService";
import WebSocketService from "../services/webSocketService";

// Nombre d'aigrettes dans le décor
const SEEDS = 16;

const random = (min, max) => min + Math.random() * (max - min);

/** Aigrettes : taille, hauteur de départ, vitesse et dérive propres à chacune */
function buildSeeds() {
  return Array.from({length: SEEDS}, (unused, id) => {
    // Les plus petites sont plus lointaines : plus lentes, plus pâles, plus floues
    const depth = Math.random();
    const size = 22 + depth * 46;
    const duration = random(34, 52) - depth * 18;
    return {
      id,
      style: {
        "--size": `${size.toFixed(0)}px`,
        "--top": `${random(8, 96).toFixed(1)}%`,
        "--lift": `${random(-46, -8).toFixed(1)}vh`,
        "--tilt": `${random(-28, 34).toFixed(0)}deg`,
        "--spin": `${random(30, 110).toFixed(0)}deg`,
        "--opacity": (0.22 + depth * 0.5).toFixed(2),
        "--blur": `${((1 - depth) * 1.6).toFixed(1)}px`,
        "--duration": `${duration.toFixed(1)}s`,
        // Délai négatif : le décor est déjà en mouvement à l'ouverture de la page
        "--delay": `${(-random(0, duration)).toFixed(1)}s`,
        "--sway": `${random(3.5, 7).toFixed(1)}s`,
      },
    };
  });
}

/** Soies de l'aigrette : un éventail de traits au sommet de la tige */
const RAYS = Array.from({length: 13}, (unused, index) => {
  const angle = (-78 + index * 13) * Math.PI / 180;
  const length = 19 - Math.abs(index - 6) * 0.7;
  return {x: (20 + Math.sin(angle) * length).toFixed(1), y: (24 - Math.cos(angle) * length).toFixed(1)};
});

export default {

  data() {
    return {
      user: new User("", ""),
      message: null,
      loading: false,
      showPassword: false,
      capsLock: false,
      // Carte secouée à chaque échec de connexion
      shaking: false,
      // Connexion réussie : la carte s'efface avant d'ouvrir le tableau de bord
      leaving: false,
      // Moyen de connexion biométrique de l'appareil (« Face ID »…), null si l'appareil n'en a pas
      // Initialisé tout de suite (sans attendre la détection) : la page s'ouvre directement sur le bon mode
      biometric: WebauthnService.device() ? WebauthnService.label() : null,
      biometricLoading: false,
      // Cet appareil a une clé Pappus, et l'utilisateur de cette clé
      deviceKnown: !!WebauthnService.device(),
      deviceUser: WebauthnService.deviceUser(),
      // Formulaire du mot de passe demandé (solution de secours)
      passwordMode: false,
      seeds: buildSeeds(),
      rays: RAYS,
      // Position du pointeur, de -1 à 1 : le décor se décale légèrement
      pointer: {x: 0, y: 0},
    };
  },
  methods: {
    async _login() {
      if (this.user.username && this.user.password) {
        this.loading = true
        this.message = null
        try {
          await AuthService.login(this.user)
          await this.enter()
        } catch (error) {
          this.leaving = false
          this.message = error.message
          this.shaking = true
        } finally {
          this.loading = false
        }
      }
    },
    /** Connexion réussie : la carte s'efface, puis le tableau de bord remplace la page de connexion */
    async enter() {
      this.leaving = true
      await new Promise((resolve) => setTimeout(resolve, 280))
      await NavigationService.replaceWithDashBoard()
    },
    /** auto : lancée d'elle-même à l'ouverture ; une annulation laisse alors simplement le formulaire */
    /** Solution de secours : le formulaire du mot de passe */
    usePassword() {
      this.passwordMode = true
      this.message = null
    },
    async biometricLogin(auto = false) {
      this.biometricLoading = true
      this.message = null
      try {
        await WebauthnService.login()
        await this.enter()
      } catch (error) {
        this.leaving = false
        if (auto && WebauthnService.cancelled(error)) return
        this.message = WebauthnService.explain(error, "Connexion biométrique impossible")
        this.shaking = true
        // Clé retirée, ou refus de l'appareil autre qu'une annulation : le mot de passe prend le relais
        this.deviceKnown = !!WebauthnService.device()
        if (!this.deviceKnown || !WebauthnService.cancelled(error)) this.passwordMode = true
      } finally {
        this.biometricLoading = false
      }
    },
    onKey(event) {
      if (event.getModifierState) this.capsLock = event.getModifierState("CapsLock")
    },
    onMouseMove(event) {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => {
        this.frame = null
        this.pointer = {
          x: (event.clientX / window.innerWidth) * 2 - 1,
          y: (event.clientY / window.innerHeight) * 2 - 1,
        }
      })
    },
  },
  computed: {
    ...mapStores(useAuthStore),
    /** Mode biométrique : appareil activé, biométrie disponible, mot de passe non demandé */
    biometricMode() {
      return !!this.biometric && this.deviceKnown && !this.passwordMode
    },
    computedAppTarget() {
      return AppEnvService.getEnv("APP_TARGET");
    },
    greeting() {
      const hour = new Date().getHours()
      if (hour < 5 || hour >= 18) return "Bonsoir"
      return hour < 12 ? "Bonjour" : "Bon après-midi"
    },
    parallax() {
      return {transform: `translate3d(${(-this.pointer.x * 14).toFixed(1)}px, ${(-this.pointer.y * 10).toFixed(1)}px, 0)`}
    },
  },
  async mounted() {
    // La connexion biométrique n'est proposée que si l'utilisateur l'a activée sur cet appareil (fiche utilisateur),
    // que sa clé n'a pas été retirée depuis, et que l'appareil sait vérifier l'utilisateur ; sinon, le mot de passe
    WebauthnService.available()
      .then(async (available) => available && await WebauthnService.isActive())
      .then((active) => {
        this.biometric = active ? WebauthnService.label() : null
        this.deviceKnown = active
        // Appareil activé, application ouverte (pas un retour après déconnexion) : Face ID, l'empreinte ou
        // Windows Hello se lance de lui-même ; s'il est fermé, le formulaire reste
        if (active && !window.history.state?.back) this.biometricLogin(true)
      })
    await AppEnvService.list().then(()=>
        // Ensure websocketService is up
        WebSocketService.getInstance().connect()
    )
  },
  beforeUnmount() {
    if (this.frame) cancelAnimationFrame(this.frame)
  },
};
</script>

<style lang="scss">
.login-page {
  position: relative;
  padding: 16px;
  overflow: hidden;
  background: linear-gradient(125deg, #24391a, #4f7a2c 38%, #6b9a3c 62%, #a6bd39);
  background-size: 220% 220%;
  animation: login-sky 26s ease-in-out infinite alternate;
}

/* ---------- Décor ---------- */

.login-scene {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.login-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  animation: login-float 22s ease-in-out infinite alternate;

  &--a {
    top: -18vmax;
    left: -12vmax;
    width: 52vmax;
    height: 52vmax;
    background: #cfe27a;
  }
  &--b {
    right: -20vmax;
    bottom: -24vmax;
    width: 60vmax;
    height: 60vmax;
    background: #1f5a4a;
    animation-duration: 30s;
    animation-direction: alternate-reverse;
  }
  &--c {
    top: 38%;
    left: 52%;
    width: 30vmax;
    height: 30vmax;
    background: #ffd98a;
    opacity: 0.28;
    animation-duration: 36s;
  }
}

.login-seeds {
  position: absolute;
  inset: -20px;
  transition: transform 0.6s ease-out;
}

/* Une aigrette traverse l'écran de gauche à droite en s'élevant ; sa forme se balance au vent */
.login-seed {
  position: absolute;
  top: var(--top);
  left: 0;
  width: var(--size);
  opacity: 0;
  filter: blur(var(--blur));
  animation: login-drift var(--duration) linear var(--delay) infinite;
  will-change: transform, opacity;
}

.login-seed__shape {
  display: block;
  width: 100%;
  transform-origin: 50% 92%;
  animation: login-sway var(--sway) ease-in-out infinite alternate;

  line {
    stroke: #fff;
    stroke-width: 0.7;
    stroke-linecap: round;
  }
  ellipse {
    fill: #fff;
  }
}

/* ---------- Carte ---------- */

.login-frame {
  position: relative;
  width: 100%;
  max-width: 410px;

  &--shake {
    animation: login-shake 0.45s ease both;
  }
}

.login-device-user {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  opacity: 0.85;
}
.login-card {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  box-shadow: 0 24px 60px rgba(14, 30, 8, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.7);
  animation: login-enter 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  transition: opacity 0.28s ease, transform 0.28s ease;
}

/* Connexion réussie : la carte s'efface vers le haut */
.login-page--leaving .login-card {
  opacity: 0;
  transform: translateY(-18px) scale(0.97);
}

.login-head {
  padding-top: 26px;
  text-align: center;
}

.login-filters {
  position: absolute;
  width: 0;
  height: 0;
}

.login-logo-frame {
  width: 62%;
  max-width: 210px;
  margin: 0 auto 8px;
}

/* Le pissenlit se balance sur sa tige ; son fond blanc est rendu transparent (filtre login-ink) */
.login-logo {
  display: block;
  width: 100%;
  filter: url(#login-ink);
  transform-origin: 8% 96%;
  animation: login-breeze 7s ease-in-out infinite alternate;
}

.login-title {
  font-size: 30px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #35541f;
}

.login-greeting {
  margin-top: 2px;
  font-size: 14px;
  color: #6f7a66;
}

.login-field {
  .q-field__control {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.7);
    transition: box-shadow 0.25s ease, background 0.25s ease;
  }
  &.q-field--focused .q-field__control {
    background: #fff;
    box-shadow: 0 6px 18px rgba(107, 154, 60, 0.22);
  }
}

.login-error {
  background: rgba(211, 47, 47, 0.1);
  color: #c62828;
}
.login-error-enter-active,
.login-error-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.login-error-enter-from,
.login-error-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Bouton : reflet qui le traverse, flèche qui avance au survol */
.login-btn {
  height: 46px;
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 8px 20px rgba(107, 154, 60, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -60%;
    width: 40%;
    height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.45), transparent);
    transform: skewX(-20deg);
    animation: login-shine 4.5s ease-in-out infinite;
    pointer-events: none;
  }
  .q-icon {
    transition: transform 0.25s ease;
  }
  &:not(.disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 26px rgba(107, 154, 60, 0.45);

    .q-icon {
      transform: translateX(4px);
    }
  }
  &.disabled {
    box-shadow: none;

    &::after {
      display: none;
    }
  }
}

.login-foot {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #8a9483;
}

/* Les éléments de la carte apparaissent l'un après l'autre */
.login-rise {
  animation: login-rise 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: calc(0.18s + var(--step, 0) * 0.07s);
}

/* ---------- Mode sombre ---------- */

.body--dark {
  .login-card {
    border-color: rgba(255, 255, 255, 0.14);
    background: rgba(24, 30, 20, 0.78);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }
  /* Pissenlit en traits clairs sur la carte sombre */
  .login-logo {
    filter: url(#login-ink-light);
  }
  .login-title {
    color: #cfe6b4;
  }
  .login-greeting,
  .login-foot {
    color: #9aa592;
  }
  .login-field .q-field__control,
  .login-field.q-field--focused .q-field__control {
    background: rgba(255, 255, 255, 0.06);
  }
  .login-error {
    background: rgba(239, 83, 80, 0.16);
    color: #ff8a80;
  }
}

/* ---------- Animations ---------- */

@keyframes login-sky {
  from { background-position: 0% 30%; }
  to { background-position: 100% 70%; }
}

@keyframes login-float {
  from { transform: translate3d(0, 0, 0) scale(1); }
  to { transform: translate3d(6vmax, 4vmax, 0) scale(1.15); }
}

@keyframes login-drift {
  0% { transform: translate3d(-14vw, 0, 0) rotate(var(--tilt)); opacity: 0; }
  8% { opacity: var(--opacity); }
  92% { opacity: var(--opacity); }
  100% { transform: translate3d(114vw, var(--lift), 0) rotate(calc(var(--tilt) + var(--spin))); opacity: 0; }
}

@keyframes login-sway {
  from { transform: rotate(-9deg) translateY(-5px); }
  to { transform: rotate(10deg) translateY(6px); }
}

@keyframes login-breeze {
  from { transform: rotate(-1.4deg); }
  to { transform: rotate(1.8deg); }
}

@keyframes login-enter {
  from { opacity: 0; transform: translateY(28px) scale(0.96); }
  to { opacity: 1; transform: none; }
}

@keyframes login-rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
}

@keyframes login-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-9px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(3px); }
}

@keyframes login-shine {
  0%, 55% { left: -60%; }
  85%, 100% { left: 130%; }
}

/* Animations réduites à la demande du système : décor immobile, pas d'entrée en scène */
@media (prefers-reduced-motion: reduce) {
  .login-page,
  .login-glow,
  .login-logo,
  .login-seed__shape,
  .login-btn::after {
    animation: none;
  }
  .login-seed {
    animation: none;
    left: calc(var(--top) * 0.9);
    opacity: var(--opacity);
    transform: rotate(var(--tilt));
  }
  .login-card,
  .login-rise {
    animation: none;
  }
  /* Durée nulle plutôt qu'aucune animation : la fin de l'animation remet le cadre au repos */
  .login-frame--shake {
    animation-duration: 0.01s;
  }
  .login-seeds {
    transition: none;
    transform: none !important;
  }
}
</style>
